import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import '../../styles/chatscope-theme.css';
import { MainContainer, ChatContainer, MessageList, Message, MessageInput, ConversationHeader } from '@chatscope/chat-ui-kit-react';
import { useTheme } from 'tamagui';

import { CobuStack } from '@cobu/ui';
import { Strings } from '../../constants';
import { useSuggestGroup } from '../../hooks/groups/useSuggestGroup';
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight';

const strings = Strings.get();

// Convert hex color to rgba with specified opacity
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function SuggestGroupScreen() {
  useVisualViewportHeight();
  const navigate = useNavigate();
  const theme = useTheme();
  const messageListRef = useRef<HTMLDivElement>(null);
  const handleNavigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const {
    messages,
    placeholder,
    onSend,
    onBackToApp,
    initializeConversation,
    shouldShowInputToolbar,
  } = useSuggestGroup({
    onNavigateBack: handleNavigateBack,
  });

  // Get theme colors - using neutral colors instead of blue/green
  const backgroundColor = '#FFFFFF'; // White background
  const messageBubbleColor = '#F6F6F6'; // Light gray for message bubbles
  const textColor = '#000000'; // Black text
  const placeholderColor = '#808080'; // Gray for placeholder text (not green)
  const borderColor = '#DDDDDD'; // Gray for borders (not blue/green)
  const hoverColor = '#000000'; // Black for hover states (not green)

  useEffect(() => {
    initializeConversation();
  }, [initializeConversation]);

  // Update CSS custom properties with theme colors
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--cs-color-link', textColor); // Use black instead of green
    root.style.setProperty('--cs-color-topaz', backgroundColor); // Use white instead of blue
    root.style.setProperty('--cs-color-link-placeholder', placeholderColor);
    root.style.setProperty('--cs-color-background', backgroundColor);
    root.style.setProperty('--cs-color-message-bubble', messageBubbleColor);
    root.style.setProperty('--cs-color-text', textColor);
    root.style.setProperty('--cs-color-border', borderColor);
    root.style.setProperty('--cs-color-hover', hoverColor);
  }, [backgroundColor, messageBubbleColor, textColor, placeholderColor, borderColor, hoverColor]);

  return (
    <CobuStack f={1} w="100%" mih={0} mah={`calc(var(--vvh) - 48px)`}>
      <MainContainer
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <CobuStack f={1} w="100%" px="$4" pt="$3" pb="$4" gap="$3">
          <ChatContainer
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <ConversationHeader>
              <ConversationHeader.Back onClick={onBackToApp} />
              <ConversationHeader.Content userName={strings.groups.suggest.title} />
            </ConversationHeader>
            <MessageList
              ref={messageListRef}
              style={{
                flex: 1,
                height: '100%',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {messages.map((m) => (
                <Message
                  key={m.id}
                  model={{
                    message: m.message,
                    direction: m.direction,
                    sender: m.sender,
                    sentTime: m.createdAt ? new Date(m.createdAt).toLocaleTimeString() : undefined,
                    position: 'single',
                  }}
                />
              ))}
            </MessageList>
            {shouldShowInputToolbar && (
              <MessageInput
                attachButton={false}
                placeholder={placeholder}
                onSend={(val) => onSend(typeof val === 'string' ? val.trim() : '')}
                style={{ width: '100%' }}
              />
            )}
          </ChatContainer>
        </CobuStack>
      </MainContainer>
    </CobuStack>
  );
} 